import {
  checkLink, latestWaypoint, parseChatLink, readableDate, resultHash, type ChatLink, type LinkCheck,
} from '@map-my-money/views';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { t, type MessageKey } from '../i18n/index.ts';
import { todayString } from '../lib/clock.ts';
import { useStore } from '../state/store.tsx';
import { Icon } from './Icon.tsx';

export function useChatLink(): ChatLink {
  const { search } = useLocation();
  return useMemo(() => parseChatLink(search), [search]);
}

/** The result hash of a views result, computed with WebCrypto (async); null until it is ready. */
export function useResultHash(result: Record<string, unknown> | null): string | null {
  const [hash, setHash] = useState<{ readonly of: object; readonly value: string } | null>(null);
  useEffect(() => {
    if (!result) return;
    let live = true;
    void resultHash(result).then((value) => {
      if (live) setHash({ of: result, value });
    });
    return () => {
      live = false;
    };
  }, [result]);
  return result && hash?.of === result ? hash.value : null;
}

/** "Plan v12 · as of 10 Oct 2026 · result 7f3ac91e": the stamp the assistant's answer ends with, for the result on this screen. */
export function ResultStamp({ result }: { result: Record<string, unknown> | null }) {
  const { version } = useStore();
  const hash = useResultHash(result);
  const vars = { version, date: readableDate(todayString()), hash: hash ?? '' };
  return (
    <p className="result-stamp" aria-label={t('stamp.label')} data-testid="result-stamp" data-hash={hash ?? ''}>
      {result && hash ? t('stamp.line', vars) : t('stamp.lineNoHash', vars)}
    </p>
  );
}

const MESSAGE: Record<LinkCheck['status'], MessageKey | null> = {
  same: 'chat.banner.same',
  same_version: 'chat.banner.sameVersion',
  version_changed: 'chat.banner.versionChanged',
  waypoint_changed: 'chat.banner.waypointChanged',
  different: 'chat.banner.different',
  unchecked: null,
};

/**
 * Shown when the page was opened from a chat result's link: says whether the page still shows what
 * the chat said. `recompute` builds this screen's result at the chat's as-of day, so a mismatch at
 * the same plan version and waypoint is a parity failure, not a moved-on plan.
 */
export function ChatBanner({ recompute, note, className = '' }: { recompute?: (asOf: string) => Record<string, unknown> | null; note?: string | null; className?: string }) {
  const link = useChatLink();
  const { version, waypoints } = useStore();
  const [dismissed, setDismissed] = useState(false);
  const today = todayString();
  const chatAsOf = link.asOf ?? today;
  const replay = useMemo(() => (link.fromChat && link.hash && recompute ? recompute(chatAsOf) : null), [link.fromChat, link.hash, recompute, chatAsOf]);
  const recomputed = useResultHash(replay);
  const check = checkLink(link, { version, waypointId: latestWaypoint(waypoints)?.id ?? null, recomputed });
  useEffect(() => {
    if (check.status !== 'different') return;
    console.warn(JSON.stringify({ event: 'parity.mismatch', planId: link.planId, version, asOf: chatAsOf, chat: link.hash, web: recomputed, path: window.location.pathname }));
  }, [check.status, link.planId, link.hash, version, chatAsOf, recomputed]);
  if (!link.fromChat || dismissed) return null;
  const message = MESSAGE[check.status];
  const alarm = check.status === 'different';
  return (
    <section className={`chat-banner ${alarm ? 'chat-banner--alarm' : ''} ${className}`} aria-label={t('chat.banner.label')} data-testid="chat-banner" data-status={check.status}>
      <span className="chat-banner__icon" aria-hidden="true"><Icon name="chat" size={18} /></span>
      <div className="chat-banner__body">
        <p className="chat-banner__title">{t('chat.banner.title')}</p>
        {message && (
          <p className={`chat-banner__status ${alarm ? 'chat-banner__status--alarm' : ''}`} role={alarm ? 'alert' : 'status'}>
            {alarm && <span aria-hidden="true">◆ </span>}
            {t(message, { version: check.status === 'version_changed' ? check.current : version })}
            {check.status === 'version_changed' && link.version !== null && (
              <>
                {' '}
                <Link to={`/connections?since=${link.version}#history`} className="text-link" data-testid="chat-see-changes">{t('chat.banner.seeChanges')}</Link>
              </>
            )}
          </p>
        )}
        {link.asOf && link.asOf !== today && (
          <p className="chat-banner__note">{t('chat.banner.otherDay', { chat: readableDate(link.asOf), today: readableDate(today) })}</p>
        )}
        {note && <p className="chat-banner__note" data-testid="chat-banner-note">{note}</p>}
      </div>
      <button type="button" className="icon-button chat-banner__close" onClick={() => setDismissed(true)} aria-label={t('chat.banner.dismiss')}>
        <Icon name="close" size={16} />
      </button>
    </section>
  );
}
